import { useState } from "react"
import { Link } from "@inertiajs/react"
import { CheckCircle2 } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { FieldRenderer } from "@/components/tuw/post/FieldRenderer"
import { ErrorLine, UploadControl } from "@/components/tuw/openHouse/UploadControl"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  BRIEF_FIELDS,
  BRIEF_WORDS,
  MISSING,
  REFERENCE_ACCEPT,
  briefCopy,
  isReferenceFile,
  saveBrief,
} from "@/lib/openHouse/brief"
import { COVER_FILE, coverQuestions, missingCover, spreadStyle, styleAnswer } from "@/lib/openHouse/coverBrief"
import * as options from "@/lib/post/options"

// Open House — the brief an order owes before a freelancer's request is
// accepted (phase-1-wording §6): the writer brief, the sample brief or the
// editing preferences, with only the answers the server asks this order for
// (`brief.fields`) and their saved values. Answers save through the JSON brief
// endpoint (OrderBriefsController): "Save and return later" saves, the finish
// button completes. The reference file saves as soon as it is chosen, through
// today's direct upload. A finished brief reopens here until the order is paid;
// after that the server sends the customer to the project instead.
//
// The final title is the order's own title, which holds the working topic until
// the customer gives a final one, so it shows that topic and a blank final
// title is never sent: leaving it blank keeps the title.
//
// A bundle's cover brief renders today's cover questions (lib/openHouse/
// coverBrief.js), says its title, subtitle and author come from the writer
// brief, and shows the server's own refusal on the answer it names.

const FILE_KEY = "reference_files"
const COVER_CTX = { options, bundle: true }

const blank = (value) => !String(value ?? "").trim()

function textAnswers(brief) {
  const answers = { ...brief.answers }
  delete answers[FILE_KEY]
  return answers
}

function FieldHead({ id, label, help }) {
  return (
    <>
      <Label htmlFor={id} className="text-[12.5px] font-bold leading-snug text-foreground">
        {label}
      </Label>
      {help && <p className="-mt-0.5 text-[12px] leading-snug text-muted-foreground">{help}</p>}
    </>
  )
}

function TextAnswer({ name, copy, value, error, onChange }) {
  const id = `brief-${name}`
  const Control = copy.multiline ? Textarea : Input

  return (
    <div className="flex flex-col gap-2">
      <FieldHead id={id} label={copy.label} help={copy.help} />
      <Control
        id={id}
        rows={copy.multiline ? 4 : undefined}
        value={value ?? ""}
        placeholder={copy.placeholder}
        aria-invalid={!!error}
        onChange={(e) => onChange(e.target.value)}
      />
      <ErrorLine>{error}</ErrorLine>
    </div>
  )
}

// Today's on/off card (FieldRenderer's toggle). An answer with its own choice
// line keeps its label and help above the card.
function ToggleAnswer({ name, copy, value, onChange }) {
  const field = { type: "toggle", key: name, label: copy.choice ?? copy.label, help: copy.choice ? null : copy.help }
  const control = <FieldRenderer field={field} values={{ [name]: value }} setField={(_key, on) => onChange(on)} />
  if (!copy.choice) return control

  return (
    <div className="flex flex-col gap-2">
      <FieldHead label={copy.label} help={copy.help} />
      {control}
    </div>
  )
}

const FILE_WORDS = {
  uploading: BRIEF_WORDS.files.uploading, uploaded: BRIEF_WORDS.files.uploaded, failed: BRIEF_WORDS.files.failed,
  retry: BRIEF_WORDS.files.retry, empty: BRIEF_WORDS.files.empty, button: BRIEF_WORDS.files.button,
  unsupported: BRIEF_WORDS.files.unsupported,
}
const COVER_FILE_WORDS = { ...FILE_WORDS, unsupported: BRIEF_WORDS.files.coverUnsupported }

// The cover brief's file is today's requirements file: its label and its
// supported-types line (FieldRenderer's file field).
function FileAnswer({ kind, saved, upload, onFile, onRetry, onRemove, onDiscard }) {
  const copy = BRIEF_FIELDS[FILE_KEY]
  const cover = kind === "cover"

  return (
    <div className="flex flex-col gap-2" data-testid="brief-file">
      {cover ? (
        <FieldHead label={COVER_FILE.label} help={`Supported: ${COVER_FILE.accept}`} />
      ) : (
        <>
          <FieldHead label={copy.label} help={kind === "sample" ? copy.sampleHelp : copy.help} />
          <p className="-mt-0.5 text-[12px] leading-snug text-muted-foreground">{BRIEF_WORDS.files.help}</p>
        </>
      )}
      <UploadControl
        saved={saved}
        upload={upload}
        accept={cover ? COVER_FILE.accept : REFERENCE_ACCEPT}
        words={cover ? COVER_FILE_WORDS : FILE_WORDS}
        onFile={onFile}
        onRetry={onRetry}
        onRemove={onRemove}
      />
      {upload.status === "failed" && (
        <Button variant="ghost" size="sm" className="self-start" onClick={onDiscard}>
          {BRIEF_WORDS.files.remove}
        </Button>
      )}
    </div>
  )
}

function Done({ orderId, copy }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-card px-6 py-6" role="status">
      <CheckCircle2 className="size-7 text-done" strokeWidth={1.9} />
      <h1 className="font-display text-[18px] font-bold tracking-tight text-foreground">{copy.doneHeading}</h1>
      <p className="text-[13px] leading-relaxed text-muted-foreground">{copy.doneBody}</p>
      <Button asChild className="font-semibold max-[560px]:w-full">
        <Link href={`/projects/${orderId}`}>{copy.back}</Link>
      </Button>
    </div>
  )
}

export default function OrderBrief({ order, brief: initialBrief }) {
  const [brief, setBrief] = useState(initialBrief)
  const isCover = initialBrief.kind === "cover"
  const [values, setValues] = useState(() => {
    const answers = textAnswers(initialBrief)
    return isCover ? spreadStyle(answers) : answers
  })
  const [upload, setUpload] = useState({ status: "idle", file: null })
  const [save, setSave] = useState("idle")
  const [errors, setErrors] = useState({})
  const [done, setDone] = useState(false)
  const copy = briefCopy(brief.kind)
  const keys = brief.fields.map((field) => field.key)
  const questions = isCover ? coverQuestions(brief.fields) : []
  const busy = save === "saving" || upload.status === "uploading"

  // A style slider clears the style answer's error.
  const setAnswer = (key, value, errorKey = key) => {
    setValues((current) => ({ ...current, [key]: value }))
    setErrors(({ [errorKey]: _cleared, ...rest }) => rest)
    if (save !== "saving") setSave("idle")
  }

  // An answer never given stays unsent, as does a blank final title.
  const answersToSend = () => {
    const answers = {}
    const given = { ...values, ...(isCover && { style: styleAnswer(values) }) }
    keys.filter((key) => key !== FILE_KEY && given[key] != null).forEach((key) => {
      if (key !== "final_title" || !blank(given[key])) answers[key] = given[key]
    })
    return answers
  }

  // Finishing with a required answer still blank saves the rest unfinished and
  // names what is missing; so does the server's own refusal.
  const submit = async (finish) => {
    const missing = isCover
      ? missingCover(questions, values)
      : brief.fields.filter((field) => field.required && blank(values[field.key])).map((field) => field.key)
    setSave("saving")
    setErrors({})

    let result
    try {
      result = await saveBrief(order.id, { answers: answersToSend(), finish: finish && missing.length === 0 })
    } catch {
      return setSave("failed")
    }
    if (result.brief) setBrief(result.brief)

    const refused = Object.keys(result.errors).filter((key) => key !== FILE_KEY && keys.includes(key))
    const named = result.ok && finish ? missing : refused
    if (named.length) {
      const refusal = (key) => (isCover && !result.ok ? result.errors[key] : null) ?? MISSING[key] ?? null
      setErrors(Object.fromEntries(named.map((key) => [key, refusal(key)])))
      return setSave("idle")
    }
    if (!result.ok) return setSave("failed")
    if (finish) return setDone(true)
    setSave("saved")
  }

  // The reference file saves on its own as soon as it is chosen.
  const sendFile = async (file) => {
    const accept = isCover ? COVER_FILE.accept : REFERENCE_ACCEPT
    if (!isReferenceFile(file, accept)) return setUpload({ status: "unsupported", file: null })

    setUpload({ status: "uploading", file })
    try {
      const result = await saveBrief(order.id, { answers: { [FILE_KEY]: file } })
      if (result.brief) setBrief(result.brief)
      if (result.ok) return setUpload({ status: "done", file: null })
      setUpload(result.errors[FILE_KEY] ? { status: "unsupported", file: null } : { status: "failed", file })
    } catch {
      setUpload({ status: "failed", file })
    }
  }

  const removeFile = async () => {
    if (busy) return
    try {
      const result = await saveBrief(order.id, { removeFile: true })
      if (result.brief) setBrief(result.brief)
      if (!result.ok) return setSave("failed")
      setUpload({ status: "idle", file: null })
    } catch {
      setSave("failed")
    }
  }

  if (done) {
    return (
      <div className="mx-auto w-full max-w-[720px] px-[26px] py-[22px] max-[860px]:px-4 max-[860px]:py-4">
        <Done orderId={order.id} copy={copy} />
      </div>
    )
  }

  const savedFile = brief.answers[FILE_KEY]?.filename ?? null
  const showSummary = Object.keys(errors).length > 0
  const editedBefore = values.previous_editing || !blank(values.previous_editing_details)

  const fieldFor = (key) => {
    const fieldCopy = BRIEF_FIELDS[key]
    if (key === FILE_KEY) {
      return (
        <FileAnswer
          kind={brief.kind}
          saved={savedFile}
          upload={upload}
          onFile={sendFile}
          onRetry={() => upload.file && sendFile(upload.file)}
          onRemove={removeFile}
          onDiscard={() => setUpload({ status: "idle", file: null })}
        />
      )
    }
    if (!fieldCopy) return null
    if (key === "previous_editing_details" && !editedBefore) return null
    if (fieldCopy.toggle) {
      return <ToggleAnswer name={key} copy={fieldCopy} value={!!values[key]} onChange={(on) => setAnswer(key, on)} />
    }
    return (
      <TextAnswer name={key} copy={fieldCopy} value={values[key]} error={errors[key]} onChange={(v) => setAnswer(key, v)} />
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-4 px-[26px] py-[22px] max-[860px]:px-4 max-[860px]:py-4">
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="px-6 pt-5 max-[560px]:px-4">
          <h1 className="font-display text-[18px] font-bold tracking-tight text-foreground">{copy.heading}</h1>
          <p className="mt-1 max-w-[580px] text-[13px] leading-relaxed text-muted-foreground text-pretty">{copy.intro}</p>
        </div>
        <div className="flex flex-col gap-5 px-6 pb-6 pt-5 max-[560px]:px-4">
          {isCover && <p className="text-[12.5px] leading-snug text-muted-foreground">{copy.fromBrief}</p>}
          {isCover
            ? questions.map((field) => (
              <div key={field.key} data-brief-field={field.key}>
                {field.key === FILE_KEY ? fieldFor(FILE_KEY) : (
                  <FieldRenderer
                    field={field}
                    values={values}
                    setField={(key, value) => setAnswer(key, value, field.key)}
                    error={errors[field.key]}
                    ctx={COVER_CTX}
                  />
                )}
              </div>
            ))
            : keys.map((key) => (
              <div key={key} data-brief-field={key}>
                {fieldFor(key)}
              </div>
            ))}
        </div>
      </div>

      {showSummary && <ErrorLine>{BRIEF_WORDS.summary}</ErrorLine>}
      {save === "saving" && <p role="status" className="text-[12.5px] text-muted-foreground">{copy.saving}</p>}
      {save === "saved" && <p role="status" className="text-[12.5px] font-semibold text-done">{copy.saved}</p>}
      {save === "failed" && <ErrorLine>{copy.saveFailed}</ErrorLine>}

      <div className="flex items-center justify-end gap-3 max-[560px]:flex-col-reverse max-[560px]:items-stretch">
        <Button variant="outline" disabled={busy} onClick={() => submit(false)}>
          {BRIEF_WORDS.saveLater}
        </Button>
        <Button className="font-bold" disabled={busy} onClick={() => submit(true)}>
          {copy.finish}
        </Button>
      </div>
    </div>
  )
}

OrderBrief.layout = (page) => {
  const props = page.props ?? page
  return (
    <AppShell active="Projects" crumb={["Projects", props.order?.title]}>
      {page}
    </AppShell>
  )
}
